import {
  EditCellRenderer,
  LinkCellRenderer,
  MapThumbnailCellRenderer,
  MapTypeCellRenderer,
  V2EditCellRenderer,
} from '@components';
import { Api } from '@services';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { toast } from '../../../components';

const MySwal = withReactContent(Swal);

// Legacy Map Column List
export const mapListColumnDefs = [
  {
    headerName: '',
    colId: 'unassignBeacon',
    width: 80,
    minWidth: 80,
    maxWidth: 80,
    sortable: false,
    cellClass: 'd-flex flex-row justify-content-center',
    cellRenderer: EditCellRenderer,
    suppressMovable: true,
  },
  {
    headerName: '',
    headerClass: '',
    field: 'mapType',
    colId: 'mapType',
    width: 40,
    minWidth: 40,
    maxWidth: 40,
    cellStyle: { padding: '0px' },
    sortable: false,
    suppressMovable: true,
    cellRenderer: MapTypeCellRenderer,
    cellRendererParams: (params) => ({
      iconType: 'world',
      showIcon: params.data.mapType === 'Geospatial',
    }),
    tooltipValueGetter: (params) => {
      if (params.data.mapType === 'Geospatial') {
        return 'This is a Geospatial Map associated with a real-world location.';
      }
      return null;
    },
  },
  {
    headerName: 'Map Name',
    headerClass: 'maps-header',
    field: 'name',
    colId: 'name',
    minWidth: 200,
    sortable: true,
    cellRenderer: LinkCellRenderer,
    cellRendererParams: ({ data }) => ({
      to: `/maps/${data.id}`,
      text: data.name || '--',
    }),
  },
  {
    headerName: 'Places',
    field: 'places',
    colId: 'places',
    sortable: true,
    headerComponent: 'agColumnHeader',
    headerComponentParams: {
      template: `
        <div ref="eLabel" class="ag-header-cell-label map-page-col-headers" role="presentation">
          <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
          <span class="ag-header-cell-text maps-header">Places &nbsp; </span>
          <i class="ag-header-cell-text sonarIcon sonarIcon-16 sonarIcon-place align-sub"></i>
          <span ref="eSortOrder" class="ag-header-cell-text ag-header-icon ag-sort-order"></span>
          <span ref="eSortAsc" class="ag-header-cell-text ag-header-icon ag-sort-ascending-icon"></span>
          <span ref="eSortDesc" class="ag-header-cell-text ag-header-icon ag-sort-descending-icon"></span>
          <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>
        </div>
      `,
    },
  },
  {
    headerName: 'Gateways',
    field: 'gateways',
    colId: 'gateways',
    sortable: true,
    headerComponent: 'agColumnHeader',
    headerComponentParams: {
      template: `
        <div ref="eLabel" class="ag-header-cell-label map-page-col-headers" role="presentation">
          <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
          <span class="ag-header-cell-text maps-header">Gateways &nbsp;</span>
          <i class="ag-header-cell-text sonarIcon sonarIcon-16 sonarIcon-gateway align-sub"></i>
          <span ref="eSortOrder" class="ag-header-cell-text ag-header-icon ag-sort-order"></span>
          <span ref="eSortAsc" class="ag-header-cell-text ag-header-icon ag-sort-ascending-icon"></span>
          <span ref="eSortDesc" class="ag-header-cell-text ag-header-icon ag-sort-descending-icon"></span>
          <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>
        </div>
      `,
    },
  },
  {
    headerName: 'Assets',
    field: 'assets',
    colId: 'assets',
    sortable: true,
    valueGetter(params) {
      const { data } = params;
      return data.assets + data.stationaryAssets;
    },
    headerComponent: 'agColumnHeader',
    headerComponentParams: {
      template: `
        <div ref="eLabel" class="ag-header-cell-label map-page-col-headers" role="presentation">
          <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
          <span class="ag-header-cell-text maps-header">Assets &nbsp;</span>
          <i class="ag-header-cell-text sonarIcon sonarIcon-16 sonarIcon-asset align-sub"></i>
          <span ref="eSortOrder" class="ag-header-cell-text ag-header-icon ag-sort-order"></span>
          <span ref="eSortAsc" class="ag-header-cell-text ag-header-icon ag-sort-ascending-icon"></span>
          <span ref="eSortDesc" class="ag-header-cell-text ag-header-icon ag-sort-descending-icon"></span>
          <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>
        </div>
      `,
    },
  },
  {
    headerName: '',
    headerClass: 'ag-theme-custom-text-right',
    field: 'maps',
    colId: 'maps',
    type: 'rightAligned',
    sortable: false,
    cellRenderer: MapThumbnailCellRenderer,
    cellRendererParams: ({ data }) => ({
      to: `/maps/${data.id}`,
      img64: `${window._sonar_env.REACT_APP_BASE_API_URL_V2}/api/v2/maps/${data.id}/image?size=64`,
      img512: `${window._sonar_env.REACT_APP_BASE_API_URL_V2}/api/v2/maps/${data.id}/image?size=512`,
    }),
  },
];

// Beta Map Column List
export const mapListV2ColumnDefs = (onDeleteSuccess) => [
  {
    headerName: '',
    colId: 'unassignBeacon',
    width: 100,
    minWidth: 100,
    maxWidth: 100,
    sortable: false,
    suppressMovable: true,
    cellClass: 'd-flex flex-row justify-content-center',
    cellRenderer: V2EditCellRenderer,
    cellRendererParams(params) {
      const { data, api, node } = params;
      return {
        async onClickDelete() {
          let userResponsePromise = new Promise((resolve, reject) => {
            MySwal.fire({
              allowOutsideClick: false,
              title: 'Are you sure?',
              text: 'This Map will be permanently deleted.',
              icon: 'warning',
              showCancelButton: true,
              cancelButtonText: 'No',
              confirmButtonText: 'Yes',
            })
              .then((submit) => {
                resolve(submit.isConfirmed);
              })
              .catch((err) => {
                reject(err);
              });
          });

          let userResponse;

          try {
            userResponse = await userResponsePromise;
          } catch (error) {
            userResponse = false;
            console.log(error);
          }

          if (userResponse) {
            try {
              await Api.delete(`v2/maps/${data.mapId}`);
              api.gridBodyCtrl.gridOptionsService.gridOptions.rowData.splice(node.rowIndex, 1);
              api.setRowData(api.gridBodyCtrl.gridOptionsService.gridOptions.rowData);
              onDeleteSuccess();
              toast.success('Map was successfully deleted!');
            } catch (error) {
              console.log(error);
              toast.error('Failed to delete Map.');
            }
          }
        },
      };
    },
  },
  {
    headerName: '',
    headerClass: '',
    field: 'mapType',
    colId: 'mapType',
    width: 55,
    minWidth: 55,
    maxWidth: 55,
    sortable: false,
    suppressMovable: true,
    cellRenderer: MapTypeCellRenderer,
    cellRendererParams: (params) => ({
      iconType: 'world',
      showIcon: params.data.mapType === 'Geospatial',
    }),
    tooltipValueGetter: (params) => {
      if (params.data.mapType === 'Geospatial') {
        return 'This is a Geospatial Map associated with a real-world location.';
      }
      return null;
    },
  },
  {
    headerName: 'Map Name',
    headerClass: 'maps-header',
    field: 'name',
    colId: 'name',
    minWidth: 200,
    sortable: true,
    cellRenderer: LinkCellRenderer,
    cellRendererParams: ({ data }) => ({
      to: `/maps/${data.mapId}`,
      text: data.name || '--',
    }),
  },
  {
    headerName: 'Places',
    field: 'placesCount',
    colId: 'placesCount',
    sortable: true,
    headerComponent: 'agColumnHeader',
    headerComponentParams: {
      template: `
        <div ref="eLabel" class="ag-header-cell-label map-page-col-headers" role="presentation">
          <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
          <span class="ag-header-cell-text maps-header">Places&nbsp;</span>
          <i class="ag-header-cell-text sonarIcon sonarIcon-16 sonarIcon-place align-sub"></i>
          <span ref="eSortOrder" class="ag-header-cell-text ag-header-icon ag-sort-order"></span>
          <span ref="eSortAsc" class="ag-header-cell-text ag-header-icon ag-sort-ascending-icon"></span>
          <span ref="eSortDesc" class="ag-header-cell-text ag-header-icon ag-sort-descending-icon"></span>
          <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>
        </div>
      `,
    },
  },
  {
    headerName: 'Gateways',
    field: 'gatewaysCount',
    colId: 'gatewaysCount',
    sortable: true,
    headerComponent: 'agColumnHeader',
    headerComponentParams: {
      template: `
        <div ref="eLabel" class="ag-header-cell-label map-page-col-headers" role="presentation">
          <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
          <span class="ag-header-cell-text maps-header">Gateways &nbsp;</span>
          <i class="ag-header-cell-text sonarIcon sonarIcon-16 sonarIcon-gateway align-sub"></i>
          <span ref="eSortOrder" class="ag-header-cell-text ag-header-icon ag-sort-order"></span>
          <span ref="eSortAsc" class="ag-header-cell-text ag-header-icon ag-sort-ascending-icon"></span>
          <span ref="eSortDesc" class="ag-header-cell-text ag-header-icon ag-sort-descending-icon"></span>
          <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>
        </div>
      `,
    },
  },
  {
    headerName: 'Assets',
    field: 'assetsCount',
    colId: 'assetsCount',
    sortable: true,
    headerComponent: 'agColumnHeader',
    headerComponentParams: {
      template: `
        <div ref="eLabel" class="ag-header-cell-label map-page-col-headers" role="presentation">
          <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
          <span class="ag-header-cell-text maps-header">Assets &nbsp;</span>
          <i class="ag-header-cell-text sonarIcon sonarIcon-16 sonarIcon-asset align-sub"></i>
          <span ref="eSortOrder" class="ag-header-cell-text ag-header-icon ag-sort-order"></span>
          <span ref="eSortAsc" class="ag-header-cell-text ag-header-icon ag-sort-ascending-icon"></span>
          <span ref="eSortDesc" class="ag-header-cell-text ag-header-icon ag-sort-descending-icon"></span>
          <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>
        </div>
      `,
    },
  },
  {
    headerName: '',
    headerClass: 'ag-theme-custom-text-right',
    field: 'maps',
    colId: 'maps',
    type: 'rightAligned',
    sortable: false,
    cellRenderer: MapThumbnailCellRenderer,
    cellRendererParams: ({ data }) => ({
      to: `maps/${data.mapId}`,
      img64: `${window._sonar_env.REACT_APP_BASE_API_URL_V2}/api/v2/maps/${data.mapId}/image?size=64`,
      img512: `${window._sonar_env.REACT_APP_BASE_API_URL_V2}/api/v2/maps/${data.mapId}/image?size=512`,
    }),
  },
];
